/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef, useState, forwardRef} from 'react'
import PropTypes from 'prop-types'
import Slider from 'react-slick'
import {
    AspectRatio,
    Box,
    Heading,
    Skeleton,
    Stack
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import useIntersectionObserver from '@salesforce/retail-react-app/app/hooks/use-intersection-observer'
import {useWishList} from '../../hooks/use-wish-list'
import {useCurrentCustomer} from '../../hooks/use-current-customer.js'
import {useCustomerGroups} from '../../hooks/use-customer-groups'
import {useWishlistActions} from '../../hooks/use-wishlist-action'
import useSegment from '../../integrations/segment/app/hooks/use-segment'
import ProductTile from '../../../app/components/product-tile'
import ProductTileRecentlyViewed from './product-tile-recently-viewed'
import {BlackArrow, WhiteArrow} from '../../components/icons'
import {getActiveSlideProducts} from '../../integrations/segment/app/utils/segment-utils'
import {isCustomerInProductGroups} from '../../utils/custom-utils.js'

const ProductCarousel = forwardRef(
    (
        {
            zone,
            recommender,
            products,
            title,
            shouldFetch,
            isApparelProducts,
            count,
            isProductTileRecentlyViewed,
            ...props
        },
        ref
    ) => {
        const scrollRef = useRef()
        const visibleRef = useRef()
        const {data: wishlist} = useWishList()
        const {data: customer} = useCurrentCustomer()
        const {data: customerGroups} = useCustomerGroups(customer?.isGuest)
        const customerGroupList = customer?.c_customerGroups || customerGroups?.result
        const {addItemToWishlist, removeItemFromWishlist} = useWishlistActions()
        const configs = getConfig()
        const isMobileAppRestrictedSrc =
            configs?.app?.login?.social?.mobileAppRestricted || 'mobileapp'
        const isMobileAppRestricted =
            typeof window !== 'undefined' &&
            localStorage.getItem('sourceCode') === isMobileAppRestrictedSrc
        const segment = useSegment()

        const [localProducts, setLocalProducts] = useState(products)
        const [activeSlideProducts, setActiveSlideProducts] = useState([])
        const isRecommenderOnScreen = useIntersectionObserver(visibleRef, {useOnce: true})

        const handleSlideChange = (currentSlide) => {
            const activeProducts = getActiveSlideProducts(
                currentSlide,
                recommendations,
                scrollRef,
                localProducts
            )
            setActiveSlideProducts(activeProducts)
        }

        const {
            isLoading,
            recommendations,
            getZoneRecommendations,
            getRecommendations,
            sendClickReco,
            sendViewReco
        } = useEinstein()

        useEffect(() => {
            if (isRecommenderOnScreen && (recommendations?.recs || localProducts)) {
                if (activeSlideProducts?.activeProducts?.length > 0) {
                    segment.sendProductListViewed(activeSlideProducts, '', '', visibleRef.current)
                }
            }
        }, [isRecommenderOnScreen, activeSlideProducts])

        useEffect(() => {
            if (isRecommenderOnScreen && (recommendations?.recs || localProducts)) {
                const timer = setTimeout(() => {
                    handleSlideChange()
                }, 100)
                return () => clearTimeout(timer)
            }
        }, [isRecommenderOnScreen, recommendations, localProducts])

        useEffect(() => {
            if (typeof shouldFetch === 'function' && !shouldFetch()) return

            if (zone) {
                getZoneRecommendations(zone, localProducts)
            } else if (recommender && props?.recommendationProduct) {
                getRecommendations(recommender, [props?.recommendationProduct])
            } else if (recommender && props?.recommendationCategory) {
                getRecommendations(recommender, localProducts, props?.recommendationCategory)
            } else if (recommender) {
                getRecommendations(recommender, localProducts)
            }
        }, [zone, recommender, localProducts, count])

        useEffect(() => {
            if (Array.isArray(products)) {
                const isSame =
                    products.length === localProducts?.length &&
                    products.every((val, i) => val === localProducts[i])
                if (!isSame) setLocalProducts(products)
            }
        }, [products])

        const handleClick = (onClick) => {
            if (typeof onClick === 'function') {
                onClick()
            }
            setTimeout(handleSlideChange, 100)
        }

        // Fire view event when carousel becomes visible
        useEffect(() => {
            if (isRecommenderOnScreen && recommendations?.recs) {
                sendViewReco(
                    {
                        recommenderName: recommendations?.recommenderName,
                        __recoUUID: recommendations?.recoUUID
                    },
                    recommendations.recs.map((rec) => ({id: rec?.id}))
                )
            }
        }, [isRecommenderOnScreen, recommendations])

        const NextArrow = ({className, onClick}) => (
            <Box
                className={className}
                onClick={() => handleClick(onClick)}
                tabIndex={0}
                role="button"
                onKeyDown={(e) => {
                    if (e.key === 'Enter') handleClick(onClick)
                }}
                position="absolute"
                right={'0'}
            >
                {isApparelProducts ? <WhiteArrow /> : <BlackArrow />}
            </Box>
        )

        const PrevArrow = ({className, onClick}) => (
            <Box
                className={className}
                onClick={() => handleClick(onClick)}
                tabIndex={0}
                role="button"
                onKeyDown={(e) => {
                    if (e.key === 'Enter') handleClick(onClick)
                }}
                transform="rotate(180deg)"
                position="absolute"
                right="105px"
            >
                {isApparelProducts ? <WhiteArrow /> : <BlackArrow />}
            </Box>
        )

        const sliderSettings = {
            infinite: false,
            speed: 500,
            slidesToShow: isApparelProducts ? 3 : isProductTileRecentlyViewed ? 8 : 5,
            slidesToScroll: 3,
            arrows: true,
            nextArrow: <NextArrow />,
            prevArrow: <PrevArrow />,
            responsive: [
                {
                    breakpoint: 1200,
                    settings: {
                        slidesToShow: isApparelProducts ? 2.2 : isProductTileRecentlyViewed ? 6 : 4,
                        slidesToScroll: 2,
                        arrows: true
                    }
                },
                {
                    breakpoint: 767,
                    settings: {
                        slidesToShow: isProductTileRecentlyViewed ? 3 : 1.3,
                        slidesToScroll: 1,
                        arrows: false,
                        afterChange: (currentSlide) => {
                            setTimeout(() => handleSlideChange(currentSlide), 100)
                        }
                    }
                }
            ]
        }

        let dataToRender = (recommendations?.recs || localProducts || []).slice(0, count || 10)

        dataToRender = (dataToRender ?? []).filter((product) => {
            // filter out not orderable
            if (!product?.inventory?.orderable) return false

            // filter by customer groups
            const productCustomerGroups = product?.c_customerGroups || []
            const isAllowedGroup = isCustomerInProductGroups(
                productCustomerGroups,
                customerGroupList
            )
            if (!isAllowedGroup) return false

            // filter subscription products only if customer group is "restricted"
            if (isMobileAppRestricted && product?.c_subscriptionProduct) {
                return false
            }

            return true
        })

        if (!isLoading && (!dataToRender || dataToRender.length === 0)) return null

        return (
            <Box
                position="relative"
                ref={ref}
                className={`carousel-wrapper product-wrapper carousel-arrows-pos ${
                    recommender ? 'einstein' : ''
                }`}
                data-testid="product-carousel"
            >
                <Box {...props} ref={visibleRef}>
                    {title && !isLoading && (
                        <Heading as="h2" className="recommenderHeading">
                            {title}
                        </Heading>
                    )}

                    <Slider
                        {...sliderSettings}
                        ref={scrollRef}
                        className="carousel-sg product-carousel-slider"
                    >
                        {isLoading
                            ? [0, 1, 2, 3].map((key) => (
                                  <Box
                                      key={key}
                                      flex="0 0 auto"
                                      width="275px"
                                      mx={2}
                                      style={{scrollSnapAlign: 'start'}}
                                  >
                                      <Stack>
                                          <AspectRatio ratio={1}>
                                              <Skeleton />
                                          </AspectRatio>
                                          <Skeleton height="20px" width="80%" />
                                          <Skeleton height="20px" width="50%" />
                                      </Stack>
                                  </Box>
                              ))
                            : dataToRender.map((product, index) => (
                                  <Box
                                      key={product?.id || index}
                                      className={
                                          isProductTileRecentlyViewed === true
                                              ? 'recently-viewed-block'
                                              : 'product-tile-block'
                                      }
                                      flex="0 0 auto"
                                      style={{
                                          scrollSnapAlign: 'start'
                                      }}
                                  >
                                      {isProductTileRecentlyViewed === true ? (
                                          <ProductTileRecentlyViewed
                                              product={product}
                                              aria-label={product?.name}
                                              productPosition={index}
                                              data-testid="product-scroller-item"
                                              dynamicImageProps={{
                                                  widths: ['70vw', '70vw', '40vw', '30vw']
                                              }}
                                              onClick={() =>
                                                  sendClickReco(
                                                      {
                                                          recommenderName:
                                                              recommendations?.recommenderName,
                                                          __recoUUID: recommendations?.recoUUID
                                                      },
                                                      product
                                                  )
                                              }
                                          />
                                      ) : (
                                          <ProductTile
                                              product={product}
                                              aria-label={product?.name}
                                              productPosition={index}
                                              data-testid="product-scroller-item"
                                              dynamicImageProps={{
                                                  widths: ['70vw', '70vw', '40vw', '30vw']
                                              }}
                                              enableFavourite
                                              isFavourite={wishlist?.productWishList[0]?.customerProductListItems?.some(
                                                  (item) =>
                                                      item?.productId ===
                                                      (product?.productId || product?.id)
                                              )}
                                              onFavouriteToggle={(
                                                  isFavourite,
                                                  productDetail,
                                                  event
                                              ) => {
                                                  const action = isFavourite
                                                      ? (p, event) =>
                                                            addItemToWishlist(
                                                                p,
                                                                index,
                                                                productDetail,
                                                                event
                                                            )
                                                      : (p) =>
                                                            removeItemFromWishlist(
                                                                p,
                                                                index,
                                                                productDetail
                                                            )
                                                  return action(product, event)
                                              }}
                                              onClick={() =>
                                                  sendClickReco(
                                                      {
                                                          recommenderName:
                                                              recommendations?.recommenderName,
                                                          __recoUUID: recommendations?.recoUUID
                                                      },
                                                      product
                                                  )
                                              }
                                          />
                                      )}
                                  </Box>
                              ))}
                    </Slider>
                </Box>
            </Box>
        )
    }
)

ProductCarousel.displayName = 'ProductCarousel'

ProductCarousel.propTypes = {
    title: PropTypes.any,
    zone: PropTypes.string,
    recommender: PropTypes.string,
    products: PropTypes.arrayOf(PropTypes.object),
    shouldFetch: PropTypes.func,
    className: PropTypes.string,
    onClick: PropTypes.func,
    isProductTileRecentlyViewed: PropTypes.bool,
    isApparelProducts: PropTypes.bool,
    count: PropTypes.string,
    recommendationProduct: PropTypes.object,
    recommendationCategory: PropTypes.object
}

export default ProductCarousel
